import type { ComponentProps, ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { useTheme } from '@emotion/react'
import { Trans } from '@lingui/react'

import { CheckmarkOutlineIcon } from '@twistopayments/design-system/icons/inline'

import { InlineButton, TextButton } from '../../atoms/button'
import { Tag, TagIcon, TagText } from '../../atoms/tag'

import { ResendButtonContent } from './components/resend-button-content'

type Props = {
  variant?: 'sms' | 'email'
  buttonProps?: Omit<
    ComponentProps<typeof TextButton>,
    'children' | 'onClick' | 'variant' | 'fullWidth'
  >
  children: ReactNode
  onClick: () => void
  actionAvailableAt?: string
  success?: boolean
  setSuccess: (value: boolean) => void
}

const smsResendDelay = 2000

export const ResendButton = ({
  buttonProps,
  children,
  onClick,
  actionAvailableAt,
  success = false,
  setSuccess,
  variant = 'sms',
}: Props) => {
  const theme = useTheme()
  const [showTag, setShowTag] = useState(success)
  const [actionAvailable, setActionAvailable] = useState(false)

  useEffect(() => {
    setActionAvailable(false)
  }, [actionAvailableAt])

  useEffect(() => {
    setShowTag(success)

    if (success) {
      const id = setTimeout(() => {
        setSuccess(false)
      }, smsResendDelay)

      return () => clearTimeout(id)
    }
  }, [setSuccess, success])

  const handleClick = () => {
    onClick()
  }

  if (showTag) {
    return (
      <Tag variant="success">
        <TagIcon icon={CheckmarkOutlineIcon} />
        <TagText>
          {variant === 'sms' ? (
            <Trans id="resendSmsButton.success" />
          ) : (
            <Trans id="resendSmsButton.success.email" />
          )}
        </TagText>
      </Tag>
    )
  }

  const content = !actionAvailable ? (
    <ResendButtonContent
      actionAvailableAt={actionAvailableAt}
      setActionAvailable={setActionAvailable}
      variant={variant}
    />
  ) : children

  if (theme.presentation.action.resend === 'inline') {
    const { color: _color, size: _size, ...inlineProps } = buttonProps ?? {}

    return (
      <InlineButton
        {...inlineProps}
        data-testid="resend-button"
        disabled={!actionAvailable || buttonProps?.disabled}
        onClick={handleClick}
      >
        {content}
      </InlineButton>
    )
  }

  return (
    <TextButton
      fullWidth
      data-testid="resend-button"
      variant="outlined"
      {...buttonProps}
      disabled={!actionAvailable || buttonProps?.disabled}
      onClick={handleClick}
    >
      {content}
    </TextButton>
  )
}
